import { useState, useEffect } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, query, where, orderBy, onSnapshot, DocumentData, QuerySnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getUserProfile, UserProfile } from '../services/userService';
import { Plant, Seed, Space } from '../types/shared';

export interface GardenData {
    user: User | null;
    plants: Plant[];
    seeds: Seed[];
    spaces: Space[];
    userProfile: UserProfile;
    loading: boolean;
}

const useGardenData = (): GardenData => {
    const [user, setUser] = useState<User | null>(null);
    const [plants, setPlants] = useState<Plant[]>([]);
    const [seeds, setSeeds] = useState<Seed[]>([]);
    const [spaces, setSpaces] = useState<Space[]>([]);
    const [userProfile, setUserProfile] = useState<UserProfile>({ location: '' });
    const [loading, setLoading] = useState<boolean>(true); // Overall loading state
    const [initialPlantsLoaded, setInitialPlantsLoaded] = useState(false);
    const [initialSeedsLoaded, setInitialSeedsLoaded] = useState(false);
    const [initialSpacesLoaded, setInitialSpacesLoaded] = useState(false);
    const [authResolved, setAuthResolved] = useState(false);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                try {
                    const profile = await getUserProfile(currentUser.uid);
                    setUserProfile(profile || { location: '' });
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                }
            } else {
                setPlants([]);
                setSeeds([]);
                setSpaces([]);
                setUserProfile({ location: '' });
            }
            setAuthResolved(true);
        });

        return () => unsubscribeAuth();
    }, []);

    // Subscribe to Plants
    useEffect(() => {
        if (!user) return;

        const q = query(collection(db, 'plants'), where('userId', '==', user.uid));
        const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
            setPlants(snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as Plant))
                .filter(plant => (plant.count ?? 1) > 0)
            );
            setInitialPlantsLoaded(true);
        }, (error) => {
            console.error("Error subscribing to plants:", error);
            setInitialPlantsLoaded(true);
        });

        return () => unsubscribe();
    }, [user]);

    // Subscribe to Seeds
    useEffect(() => {
        if (!user) return;

        const q = query(collection(db, 'seeds'), where('userId', '==', user.uid), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
            setSeeds(snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as Seed))
                .filter(seed => !seed.archived)
            );
            setInitialSeedsLoaded(true);
        }, (error) => {
            console.error("Error subscribing to seeds:", error);
            setInitialSeedsLoaded(true);
        });

        return () => unsubscribe();
    }, [user]);

    // Subscribe to Spaces
    useEffect(() => {
        if (!user) return;

        const q = query(collection(db, 'spaces'), where('userId', '==', user.uid));
        const unsubscribe = onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Space));
            // Sort by order
            setSpaces(items.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)));
            setInitialSpacesLoaded(true);
        }, (error) => {
            console.error("Error subscribing to spaces:", error);
            setInitialSpacesLoaded(true);
        });

        return () => unsubscribe();
    }, [user]);

    // Combined loading state: wait for auth AND all initial snapshots (if logged in)
    useEffect(() => {
        if (!authResolved) return;

        if (!user) {
            setLoading(false);
            return;
        }

        if (initialPlantsLoaded && initialSeedsLoaded && initialSpacesLoaded) {
            setLoading(false);
        }
    }, [authResolved, user, initialPlantsLoaded, initialSeedsLoaded, initialSpacesLoaded]);

    return {
        user,
        plants,
        seeds,
        spaces,
        userProfile,
        loading,
    };
};

export default useGardenData;
